import { useState, useEffect } from 'react'
import {
  CloseButton,
  Button,
  NativeSelect,
  HStack,
  Input,
  RadioGroup,
  Stack,
  Textarea,
  Box,
  Checkbox,
  Text,
  Spinner,
  VStack,
  Field,
  Dialog,
  Portal,
  type RadioGroupValueChangeDetails,
} from '@chakra-ui/react';
import { toaster } from '@/components/ui/toaster'
import { useWorksiteSearch, useBusinesses } from '../../hooks/useBusiness'
import { useAffiliateWorksiteToUnion, useAffiliateBusinessToUnion } from '../../hooks/useUnion'
import { worksiteApi, businessApi } from '../../lib/api'
import { ContactSearchSelect } from '../shared/ContactSearchSelect'

interface AffiliateModalProps {
  isOpen: boolean
  onClose: () => void
  unionId: string
}

export function AffiliateBusinessModal({ isOpen, onClose, unionId }: AffiliateModalProps) {
  const [scope, setScope] = useState<'business' | 'worksite'>('business')
  const [selectedId, setSelectedId] = useState('')
  const [contractStartDate, setContractStartDate] = useState('')
  const [contractEndDate, setContractEndDate] = useState('')
  const [unionRepContactId, setUnionRepContactId] = useState<string | null>(null)
  const [unionRepName, setUnionRepName] = useState<string | null>(null)
  const [employerContactId, setEmployerContactId] = useState<string | null>(null)
  const [employerContactName, setEmployerContactName] = useState<string | null>(null)
  const [notes, setNotes] = useState('')
  const [excludedWorksiteIds, setExcludedWorksiteIds] = useState<string[]>([])

  const { data: standaloneWorksiteData } = useWorksiteSearch({ pageSize: 200 })
  const { data: businesses } = useBusinesses()

  // Fetch worksites for the selected business
  const { data: businessWorksiteData, isLoading: worksitesLoading } = useWorksiteSearch(
    { businessId: selectedId, pageSize: 200 },
    { enabled: scope === 'business' && !!selectedId }
  )

  const worksiteMutation = useAffiliateWorksiteToUnion(unionId)
  const businessMutation = useAffiliateBusinessToUnion(unionId)

  const isLoading = worksiteMutation.isPending || businessMutation.isPending

  // Reset exclusions when business changes
  useEffect(() => {
    setExcludedWorksiteIds([])
  }, [selectedId])

  const handleScopeChange = (value: string) => {
    setScope(value as 'business' | 'worksite')
    setSelectedId('')
    setUnionRepContactId(null)
    setUnionRepName(null)
    setEmployerContactId(null)
    setEmployerContactName(null)
    setNotes('')
    setExcludedWorksiteIds([])
  }

  const toggleWorksiteExclusion = (worksiteId: string) => {
    setExcludedWorksiteIds(prev =>
      prev.includes(worksiteId)
        ? prev.filter(id => id !== worksiteId)
        : [...prev, worksiteId]
    )
  }

  const handleSubmit = async () => {
    try {
      if (scope === 'business') {
        await businessMutation.mutateAsync({
          businessId: selectedId,
          contractStartDate: contractStartDate || undefined,
          contractEndDate: contractEndDate || undefined,
          unionRepContactId: unionRepContactId || undefined,
          unionRepName: unionRepName || undefined,
          employerContactId: employerContactId || undefined,
          employerContactName: employerContactName || undefined,
          excludedWorksiteIds: excludedWorksiteIds.length > 0 ? excludedWorksiteIds : undefined,
        })
        // Save business notes if provided
        if (notes.trim()) {
          await businessApi.updateNotes(selectedId, { notes: notes.trim() })
        }
        toaster.create({ title: 'Business affiliation added', type: 'success', duration: 3000 })
      } else {
        await worksiteMutation.mutateAsync({
          worksiteId: selectedId,
          contractStartDate: contractStartDate || undefined,
          contractEndDate: contractEndDate || undefined,
          unionRepContactId: unionRepContactId || undefined,
          unionRepName: unionRepName || undefined,
          employerContactId: employerContactId || undefined,
          employerContactName: employerContactName || undefined,
        })
        // Save worksite notes if provided
        if (notes.trim()) {
          await worksiteApi.updateNotes(selectedId, { notes: notes.trim() })
        }
        toaster.create({ title: 'Worksite affiliation added', type: 'success', duration: 3000 })
      }
      onClose()
    } catch {
      toaster.create({ title: 'Failed to add affiliation', type: 'error', duration: 3000 })
    }
  }

  return (
    <Dialog.Root open={isOpen} size='lg' onOpenChange={e => {
      if (!e.open) {
        onClose();
      }
    }}>
      <Portal>

        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content>
            <Dialog.Header>Add Affiliation</Dialog.Header>
            <Dialog.CloseTrigger asChild><CloseButton size="sm" /></Dialog.CloseTrigger>
            <Dialog.Body>
              <Field.Root mb={4}>
                <Field.Label fontSize="sm">Scope</Field.Label>
                <RadioGroup.Root size="sm" value={scope} onValueChange={(details: RadioGroupValueChangeDetails) => handleScopeChange(details.value ?? scope)}>
                  <Stack direction="row" gap={4}>
                    <RadioGroup.Item value="business" colorPalette="purple"><RadioGroup.ItemHiddenInput /><RadioGroup.ItemIndicator /><RadioGroup.ItemText>Business</RadioGroup.ItemText></RadioGroup.Item>
                    <RadioGroup.Item value="worksite" colorPalette="brand"><RadioGroup.ItemHiddenInput /><RadioGroup.ItemIndicator /><RadioGroup.ItemText>Standalone Worksite</RadioGroup.ItemText></RadioGroup.Item>
                  </Stack>
                </RadioGroup.Root>
              </Field.Root>

              {scope === 'business' ? (
                <Field.Root required mb={4}>
                  <Field.Label fontSize="sm">Business</Field.Label>
                  <NativeSelect.Root size="sm">
                    <NativeSelect.Field
                      placeholder="Select a business"
                      value={selectedId}
                      onChange={e => setSelectedId(e.target.value)}>
                      {businesses?.map(b => (
                        <option key={b.id} value={b.id}>
                          {b.label} {b.type ? `(${b.type})` : ''}
                        </option>
                      ))}
                    </NativeSelect.Field>
                    <NativeSelect.Indicator />
                  </NativeSelect.Root>
                </Field.Root>
              ) : (
                <Field.Root required mb={4}>
                  <Field.Label fontSize="sm">Worksite</Field.Label>
                  <NativeSelect.Root size="sm">
                    <NativeSelect.Field
                      placeholder="Select a worksite"
                      value={selectedId}
                      onChange={e => setSelectedId(e.target.value)}>
                      {standaloneWorksiteData?.items.map(w => (
                        <option key={w.id} value={w.id}>
                          {w.label} {w.city ? `(${w.city})` : ''}
                        </option>
                      ))}
                    </NativeSelect.Field>
                    <NativeSelect.Indicator />
                  </NativeSelect.Root>
                </Field.Root>
              )}

              <HStack gap={4} mb={4}>
                <Field.Root>
                  <Field.Label fontSize="sm">Union Rep</Field.Label>
                  <Input
                    size="sm"
                    placeholder="Union rep name..."
                    value={unionRepName || ''}
                    onChange={e => { setUnionRepName(e.target.value); setUnionRepContactId(null) }}
                  />
                </Field.Root>
                <Field.Root>
                  <Field.Label fontSize="sm">Employer Contact</Field.Label>
                  <ContactSearchSelect
                    value={employerContactId || undefined}
                    displayName={employerContactName || undefined}
                    onSelect={(id, name) => {
                      setEmployerContactId(id)
                      setEmployerContactName(name)
                    }}
                    placeholder="Search for employer contact..."
                    allowFreeText
                  />
                </Field.Root>
              </HStack>

              <HStack gap={4} mb={4}>
                <Field.Root>
                  <Field.Label fontSize="sm">Contract Start</Field.Label>
                  <Input
                    size="sm"
                    type="date"
                    value={contractStartDate}
                    onChange={e => setContractStartDate(e.target.value)}
                  />
                </Field.Root>
                <Field.Root>
                  <Field.Label fontSize="sm">Contract End</Field.Label>
                  <Input
                    size="sm"
                    type="date"
                    value={contractEndDate}
                    onChange={e => setContractEndDate(e.target.value)}
                  />
                </Field.Root>
              </HStack>

              <Field.Root mb={4}>
                <Field.Label fontSize="sm">Notes</Field.Label>
                <Textarea
                  size="sm"
                  rows={3}
                  placeholder={scope === 'business' ? 'Notes about this business...' : 'Notes about this worksite...'}
                  value={notes}
                  onChange={e => setNotes(e.target.value)}
                />
              </Field.Root>

              {/* Worksite checklist for business scope */}
              {scope === 'business' && selectedId && (
                <Field.Root>
                  <Field.Label fontSize="sm">
                    Covered Worksites
                    {businessWorksiteData && (
                      <Text as="span" color="gray.500" fontWeight="normal" ml={1}>
                        ({businessWorksiteData.items.length - excludedWorksiteIds.length} of {businessWorksiteData.items.length} covered)
                      </Text>
                    )}
                  </Field.Label>
                  <Box
                    maxH="250px"
                    overflowY="auto"
                    border="1px solid"
                    borderColor="gray.200"
                    borderRadius="md"
                    p={2}
                  >
                    {worksitesLoading ? (
                      <VStack py={4}>
                        <Spinner size="sm" color="accent" />
                      </VStack>
                    ) : businessWorksiteData?.items.length === 0 ? (
                      <Text fontSize="sm" color="gray.500" p={2}>No worksites found for this business</Text>
                    ) : (
                      businessWorksiteData?.items.map(w => (
                        <HStack key={w.id} py={1} px={1} _hover={{ bg: 'gray.50' }} borderRadius="sm">
                          <Checkbox.Root
                            size="sm"
                            colorPalette="green"
                            onCheckedChange={() => toggleWorksiteExclusion(w.id)}
                            checked={!excludedWorksiteIds.includes(w.id)}><Checkbox.HiddenInput /><Checkbox.Control><Checkbox.Indicator /></Checkbox.Control></Checkbox.Root>
                          <Text fontSize="sm" flex={1}>{w.label}</Text>
                          {w.city && <Text fontSize="xs" color="gray.500">{w.city}</Text>}
                        </HStack>
                      ))
                    )}
                  </Box>
                </Field.Root>
              )}
            </Dialog.Body>
            <Dialog.Footer>
              <Button variant="ghost" mr={3} onClick={onClose}>Cancel</Button>
              <Button
                colorPalette={scope === 'business' ? 'purple' : 'blue'}
                onClick={handleSubmit}
                loading={isLoading}
                disabled={!selectedId}
              >
                Add Affiliation
              </Button>
            </Dialog.Footer>
          </Dialog.Content>
        </Dialog.Positioner>

      </Portal>
    </Dialog.Root>
  );
}
